import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import type {
  DashboardConfig,
  WidgetConfig,
  TableWidgetConfig,
} from "../types/dashboard";
import type {
  Dashboard,
  DashboardWidget,
  DashboardPosition,
} from "@/services/api/dashboard/types";
import * as DashboardAPI from "@/services/api/dashboard";

const CURRENT_DASHBOARD_KEY = "current_dashboard_id";

interface DashboardContextType {
  dashboards: DashboardConfig[];
  currentDashboard: DashboardConfig | null;
  setCurrentDashboard: (dashboard: DashboardConfig | null) => void;
  addWidget: (dashboardId: string, widget: WidgetConfig) => Promise<void>;
  removeWidget: (dashboardId: string, widgetId: string) => Promise<void>;
  updateWidgetPosition: (
    dashboardId: string,
    widgetId: string,
    position: { x: number; y: number; width: number; height: number }
  ) => Promise<void>;
  refreshDashboards: () => Promise<void>;
  getNextRowPosition: (dashboardId: string) => number;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined
);

const mapApiWidgetToConfig = (widget: DashboardWidget): WidgetConfig => {
  return {
    id: widget.widget_id,
    title: widget.title,
    position: {
      x: widget.position.col,
      y: widget.position.row,
      width: widget.position.width,
      height: widget.position.height,
    },
    type: "table",
    dataSource: {
      type: "screener",
      columns: [],
      filters: {},
      sortBy: "name",
      sortDirection: "asc",
      viewId: widget.config_id,
    },
  } as TableWidgetConfig;
};

const mapApiDashboardToConfig = (dashboard: Dashboard): DashboardConfig => ({
  id: dashboard.dashboard_id,
  name: dashboard.name,
  widgets: dashboard.widgets.map(mapApiWidgetToConfig),
});

const mapConfigPositionToApi = (position: {
  x: number;
  y: number;
  width: number;
  height: number;
}): DashboardPosition => ({
  row: position.y,
  col: position.x,
  width: position.width,
  height: position.height,
});

const mapApiPositionToConfig = (
  position: DashboardPosition
): { x: number; y: number; width: number; height: number } => ({
  x: position.col,
  y: position.row,
  width: position.width,
  height: position.height,
});

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dashboards, setDashboards] = useState<DashboardConfig[]>([]);
  const [currentDashboard, setCurrentDashboard] =
    useState<DashboardConfig | null>(null);

  // Load initial dashboards
  useEffect(() => {
    const loadInitialDashboards = async () => {
      try {
        const loadedDashboards = await DashboardAPI.list();
        const mappedDashboards = loadedDashboards.map(mapApiDashboardToConfig);
        setDashboards(mappedDashboards);

        const savedId = localStorage.getItem(CURRENT_DASHBOARD_KEY);
        if (savedId) {
          const savedDashboard = mappedDashboards.find((d) => d.id === savedId);
          if (savedDashboard) {
            setCurrentDashboard(savedDashboard);
          }
        }
      } catch (error) {
        console.error("Failed to load initial dashboards:", error);
      }
    };

    loadInitialDashboards();
  }, []);

  const handleSetCurrentDashboard = useCallback(
    (dashboard: DashboardConfig | null) => {
      setCurrentDashboard(dashboard);
      if (dashboard) {
        localStorage.setItem(CURRENT_DASHBOARD_KEY, dashboard.id);
      } else {
        localStorage.removeItem(CURRENT_DASHBOARD_KEY);
      }
    },
    []
  );

  const refreshDashboards = useCallback(async () => {
    try {
      const updatedDashboards = await DashboardAPI.list();
      const mappedDashboards = updatedDashboards.map(mapApiDashboardToConfig);
      setDashboards(mappedDashboards);

      if (currentDashboard) {
        const updatedCurrentDashboard = mappedDashboards.find(
          (d) => d.id === currentDashboard.id
        );
        if (updatedCurrentDashboard) {
          setCurrentDashboard(updatedCurrentDashboard);
        } else {
          handleSetCurrentDashboard(null);
        }
      }
    } catch (error) {
      console.error("Failed to refresh dashboards:", error);
    }
  }, [currentDashboard, handleSetCurrentDashboard]);

  const addWidget = useCallback(
    async (dashboardId: string, widget: WidgetConfig) => {
      try {
        await DashboardAPI.addWidget(dashboardId, {
          config_id: widget.id,
          type: widget.type,
          title: widget.title,
          position: mapConfigPositionToApi(widget.position),
        });
        await refreshDashboards();
      } catch (error) {
        console.error("Failed to add widget:", error);
        throw error;
      }
    },
    [refreshDashboards]
  );

  const removeWidget = useCallback(
    async (dashboardId: string, widgetId: string) => {
      try {
        await DashboardAPI.removeWidget(dashboardId, widgetId);
        await refreshDashboards();
      } catch (error) {
        console.error("Failed to remove widget:", error);
        throw error;
      }
    },
    [refreshDashboards]
  );

  const updateWidgetPosition = useCallback(
    async (
      dashboardId: string,
      widgetId: string,
      position: { x: number; y: number; width: number; height: number }
    ) => {
      try {
        // Convert to API position format
        const apiPosition: DashboardPosition = {
          row: position.y,
          col: position.x,
          width: position.width,
          height: position.height,
        };
        await DashboardAPI.updateWidgetPosition(
          dashboardId,
          widgetId,
          apiPosition
        );
        await refreshDashboards();
      } catch (error) {
        console.error("Failed to update widget position:", error);
        throw error;
      }
    },
    [refreshDashboards]
  );

  const getNextRowPosition = useCallback(
    (dashboardId: string): number => {
      const dashboard = dashboards.find((d) => d.id === dashboardId);
      if (!dashboard || !dashboard.widgets || dashboard.widgets.length === 0) {
        return 0;
      }

      // Find the highest row + height value from all widgets
      return Math.max(
        ...dashboard.widgets.map((widget) => {
          const position = widget.position;
          return position.y + position.height;
        })
      );
    },
    [dashboards]
  );

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      dashboards,
      currentDashboard,
      setCurrentDashboard: handleSetCurrentDashboard,
      addWidget,
      removeWidget,
      updateWidgetPosition,
      refreshDashboards,
      getNextRowPosition,
    }),
    [
      dashboards,
      currentDashboard,
      handleSetCurrentDashboard,
      addWidget,
      removeWidget,
      updateWidgetPosition,
      refreshDashboards,
      getNextRowPosition,
    ]
  );

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

/**
 * Default context values for when useDashboard is called outside of a DashboardProvider.
 * This allows components like PortfolioContributions to be safely rendered in drawers
 * without crashing.
 */
const defaultDashboardContext: DashboardContextType = {
  dashboards: [],
  currentDashboard: null,
  setCurrentDashboard: () => {},
  addWidget: async () => {},
  removeWidget: async () => {},
  updateWidgetPosition: async () => {},
  refreshDashboards: async () => {},
  getNextRowPosition: () => 0,
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  // Return default context if no provider exists (e.g., when rendered in a drawer)
  if (context === undefined) {
    return defaultDashboardContext;
  }
  return context;
};
